import { Link } from 'react-router-dom';
import { Navigation } from './Navigation';
import type { User } from './UserMenu';

/**
 * Header Props
 */
export interface HeaderProps {
  /** Current active dictionary name */
  activeDictionary?: string;
  /** Current authenticated user, or null if not logged in */
  user?: User | null;
  /** Whether user is authenticated (deprecated, use user prop instead) */
  isAuthenticated?: boolean;
  /** Whether user is admin (deprecated, use user prop instead) */
  isAdmin?: boolean;
  /** Callback when logout is triggered */
  onLogout?: () => void;
}

/**
 * Header Component
 * 
 * Responsive header with navigation for the Spellion application.
 * Features:
 * - Logo and brand name
 * - Integrated Navigation component with all routes
 * - UserMenu for authentication state and account access
 * - Mobile hamburger menu (< 768px)
 * - Desktop horizontal navigation (>= 768px)
 * - Touch-friendly targets (44x44px minimum)
 * 
 * @validates Requirements 9.1, 10.1, 10.2, 10.3
 */
export function Header({
  activeDictionary = 'ODS',
  user = null,
  isAuthenticated = false,
  isAdmin = false,
  onLogout,
}: HeaderProps) {
  return (
    <header className="sticky top-0 z-40 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 transition-colors">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            <img 
              src="/spellion.svg" 
              alt="Spellion" 
              className="w-8 h-8 rounded-lg shadow-sm"
            />
            <span className="font-display font-bold text-xl bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-400 dark:to-primary-500 bg-clip-text text-transparent">Spellion</span>
          </Link>

          {/* Navigation Component with UserMenu */}
          <Navigation
            activeDictionary={activeDictionary}
            user={user}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            onLogout={onLogout}
          />
        </div>
      </div>
    </header>
  );
}
